import {
  ChangeDetectionStrategy,
  Component,
  computed,
  effect,
  input,
  output,
  signal,
  untracked,
} from '@angular/core';
import {FormsModule} from '@angular/forms';
import {SelectButton} from 'primeng/selectbutton';
import {ToggleSwitch} from 'primeng/toggleswitch';
import {TranslocoPipe} from '@jsverse/transloco';
import {
  AuditInfo,
  CriteriaWeightingResponse,
  PairwiseComparisonRequest,
  UpsertCriteriaWeightingRequest,
  WeightingMode,
} from '@frontend/shared-api';
import {AuditInfoComponent} from '../../../../../../../shared/components/audit-info/audit-info.component';
import {ProjectStepSaveButtonComponent} from '../../../../../../../shared/project-step-save-button/project-step-save-button.component';
import {PairwiseBlockXComponent} from '../pairwise-block-x/pairwise-block-x.component';
import {CriteriaRankingXComponent} from '../criteria-ranking-x/criteria-ranking-x.component';
import {WeightingSummaryXComponent} from '../weighting-summary-x/weighting-summary-x.component';
import {PairwiseComparisonX, PairwiseItemX, RankingRowX} from '../../evaluation-criteria-x.types';

@Component({
  selector: 'app-criteria-weighting-x',
  imports: [
    FormsModule,
    SelectButton,
    ToggleSwitch,
    TranslocoPipe,
    AuditInfoComponent,
    ProjectStepSaveButtonComponent,
    PairwiseBlockXComponent,
    CriteriaRankingXComponent,
    WeightingSummaryXComponent,
  ],
  templateUrl: './criteria-weighting-x.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class CriteriaWeightingXComponent {
  protected readonly WeightingMode = WeightingMode;

  readonly translationKeyPrefix = input.required<string>();
  readonly identifierPrefix = input.required<string>();
  readonly testId = input.required<string>();
  readonly items = input.required<PairwiseItemX[]>();
  readonly comparisons = input.required<PairwiseComparisonX[]>();
  readonly weighting = input<CriteriaWeightingResponse | null>(null);
  readonly auditInfo = input<AuditInfo | null>(null);
  readonly disabled = input(false);
  readonly saving = input(false);

  readonly comparisonsSave = output<PairwiseComparisonRequest[]>();
  readonly weightingSave = output<UpsertCriteriaWeightingRequest>();

  protected readonly mode = signal<WeightingMode>(WeightingMode.RANKING);
  protected readonly equalWeights = signal(false);
  protected readonly workingRanking = signal<PairwiseItemX[]>([]);
  private readonly savedRanking = signal<PairwiseItemX[]>([]);

  protected readonly titleKey = computed<string>(
    () => `${this.translationKeyPrefix()}.weighting.title`,
  );

  protected readonly equalForAllKey = computed<string>(
    () => `${this.translationKeyPrefix()}.equalForAll`,
  );

  protected readonly equalForAllHintKey = computed<string>(
    () => `${this.translationKeyPrefix()}.weighting.equalForAllHint`,
  );

  protected readonly summaryTitleKey = computed<string>(
    () => `${this.translationKeyPrefix()}.weighting.result`,
  );

  protected readonly modeOptions = computed(() => [
    {
      value: WeightingMode.RANKING,
      translationKey: `${this.translationKeyPrefix()}.weighting.ranking`,
    },
    {
      value: WeightingMode.PAIRWISE,
      translationKey: `${this.translationKeyPrefix()}.weighting.pairwise`,
    },
  ]);

  private readonly sortedItems = computed<PairwiseItemX[]>(() =>
    [...this.items()].sort((first, second) => first.sortNumber - second.sortNumber),
  );

  protected readonly modeSelectionDisabled = computed<boolean>(
    () => this.disabled() || this.equalWeights(),
  );

  protected readonly showsRanking = computed<boolean>(
    () => !this.equalWeights() && this.mode() === WeightingMode.RANKING,
  );

  protected readonly showsPairwise = computed<boolean>(
    () => !this.equalWeights() && this.mode() === WeightingMode.PAIRWISE,
  );

  protected readonly hasRankingChanges = computed<boolean>(() => {
    const savedRanking = this.savedRanking();

    return this.workingRanking().some((item, index) => savedRanking[index]?.id !== item.id);
  });

  protected readonly showsRankingSaveButton = computed<boolean>(
    () => this.hasRankingChanges() && !this.disabled(),
  );

  protected readonly ranking = computed<RankingRowX[]>(() => {
    const itemsById = new Map(this.sortedItems().map((item) => [item.id, item]));

    return (this.weighting()?.weights ?? [])
      .flatMap((weight) => {
        const item = itemsById.get(weight.id);
        return item && weight.weight != null ? [{item, percentage: weight.weight}] : [];
      })
      .sort(
        (first, second) =>
          second.percentage - first.percentage || first.item.sortNumber - second.item.sortNumber,
      )
      .map(({item, percentage}) => ({
        id: item.id,
        identifier: item.identifier,
        translations: item.translations,
        percentage,
      }));
  });

  protected readonly showsSummary = computed<boolean>(() => this.ranking().length > 0);

  constructor() {
    effect(() => {
      const weighting = this.weighting();
      this.items();

      untracked(() => {
        this.mode.set(weighting?.mode ?? WeightingMode.RANKING);
        this.equalWeights.set(weighting?.equalWeights ?? false);

        const ranked = this.rankedFromWeighting(weighting);
        this.workingRanking.set(ranked);
        this.savedRanking.set(ranked);
      });
    });
  }

  onModeChange(mode: WeightingMode): void {
    this.mode.set(mode);
    this.emitWeighting();
  }

  onEqualWeightsChange(equalWeights: boolean): void {
    this.equalWeights.set(equalWeights);
    this.emitWeighting();
  }

  onRankingChanged(rankedItems: PairwiseItemX[]): void {
    this.workingRanking.set(rankedItems);
  }

  onRankingSave(): void {
    this.emitWeighting();
  }

  onComparisonsSave(comparisons: PairwiseComparisonRequest[]): void {
    this.comparisonsSave.emit(comparisons);
  }

  private emitWeighting(): void {
    this.weightingSave.emit({
      mode: this.mode(),
      equalWeights: this.equalWeights(),
      rankings: this.workingRanking().map((item, index) => ({
        id: item.id,
        rankPosition: index + 1,
      })),
    });
  }

  private rankedFromWeighting(weighting: CriteriaWeightingResponse | null): PairwiseItemX[] {
    const positionById = new Map(
      (weighting?.weights ?? []).map((weight) => [weight.id, weight.rankPosition]),
    );

    return [...this.sortedItems()].sort(
      (first, second) =>
        (positionById.get(first.id) ?? first.sortNumber) -
        (positionById.get(second.id) ?? second.sortNumber),
    );
  }
}
